import { useDebounce } from '@ais-ui/custom-hooks';
import {
  AutoCompleteShipInfo,
  autocompleteShips,
  useSession,
} from '@ais-ui/websockets';
import { useShipApi } from '@ais-ui/api';
import { useQueries } from '@tanstack/react-query';
import type { ShipSearchQuerystringGet200ResponseRecordsInner } from '@ais-ui/api-spec/portserver';
import { isNonEmpty } from '@ais-ui/utils';
import { useEffect, useMemo, useState } from 'react';
import { useExtendedVesselInfo } from './useExtendedVesselInfo';
import { VesselResult } from '@ais-ui/api-spec/shipdb';

export interface AisSearchResult extends AutoCompleteShipInfo {
  requestType: 'ais' | 'no-recent-ais';
}

export interface DatabaseSearchResult
  extends ShipSearchQuerystringGet200ResponseRecordsInner {
  requestType: 'database';
  filteredOut: boolean;
}

export interface ShipDbSearchResult extends VesselResult {
  shipDbResult: VesselResult;
  mmsi: number | null;
  imo: number | null;
  name: string;
  requestType: 'ship-db';
  filteredOut: boolean;
}

export type ShipSearchResult =
  | AisSearchResult
  | DatabaseSearchResult
  | ShipDbSearchResult;

interface UseShipSearchResult {
  data: ShipSearchResult[];
  isLoading: boolean;
  isError: boolean;
  error: unknown | null;
}

const capitalizeName = (name: string | null | undefined) => {
  if (!name) return '';

  return name
    .toLowerCase()
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

const convertToInteger = (value: string | number | null | undefined) => {
  if (!isNonEmpty(value)) return null;
  return parseInt(String(value).replace(/^0+/, ''), 10);
};

export const useShipSearch = (searchString: string): UseShipSearchResult => {
  const bouncedSearchString = useDebounce(searchString, 200);
  const { olSession } = useSession();
  const shipApi = useShipApi();
  const [isLoading, setIsLoading] = useState(false);

  // Use the extended vessel info hook
  const { extendedVesselInfo, isLoading: isExtendedVesselLoading } =
    useExtendedVesselInfo({
      searchString: bouncedSearchString,
      enabled: bouncedSearchString.length > 2, // Only search when we have enough characters
    });

  const memoizedExtendedVesselInfo = useMemo(() => {
    if (!extendedVesselInfo) return null;

    return {
      mmsi: convertToInteger(extendedVesselInfo.basic?.vesselMmsiNumber),
      imo: convertToInteger(extendedVesselInfo.basic?.vesselImoNumber),
      name: capitalizeName(extendedVesselInfo.basic?.vesselName) || 'N/A',
      data: extendedVesselInfo,
    };
  }, [extendedVesselInfo]);

  const queries = useQueries({
    queries: [
      {
        queryKey: ['autocompleteShips', bouncedSearchString],
        queryFn: () => {
          if (bouncedSearchString.length < 3) {
            return Promise.resolve([]);
          }
          return autocompleteShips(olSession, bouncedSearchString);
        },
        enabled: bouncedSearchString.length > 2,
      },
      {
        queryKey: ['portServerShipSearch', bouncedSearchString],
        queryFn: () => {
          if (bouncedSearchString.length < 3) {
            return Promise.resolve([]);
          }
          return Promise.race([
            shipApi.shipSearchQuerystringGet({
              querystring: bouncedSearchString,
            }),
            new Promise((_, reject) =>
              setTimeout(
                () => reject(new Error('Database search timeout')),
                10000
              )
            ),
          ]);
        },
        enabled: bouncedSearchString.length > 2,
      },
    ],
  });

  const [aisSearchResult, databaseSearchResult] = queries;

  // Include extended vessel loading in the overall loading state
  useEffect(() => {
    if (bouncedSearchString.length > 2) {
      setIsLoading(aisSearchResult.isLoading || databaseSearchResult.isLoading);
    } else {
      setIsLoading(false);
    }
  }, [
    bouncedSearchString,
    aisSearchResult.isLoading,
    databaseSearchResult.isLoading,
  ]);

  const data: ShipSearchResult[] = useMemo(() => {
    // Get AIS results (highest priority)
    const aisResults =
      aisSearchResult?.data?.map((item: AutoCompleteShipInfo) => ({
        ...item,
        name: capitalizeName(item.name),
        imo: convertToInteger(item.imo),
        mmsi: convertToInteger(item.mmsi),
        requestType: item.timedOut ? 'no-recent-ais' : ('ais' as const),
        filteredOut: item.filteredOut || false,
      })) || [];

    // Get ShipDB result (medium priority)
    const shipDbResults: ShipSearchResult[] = [];
    if (memoizedExtendedVesselInfo) {
      const extendedVesselResult: ShipDbSearchResult = {
        shipDbResult: memoizedExtendedVesselInfo.data,
        mmsi: memoizedExtendedVesselInfo.mmsi,
        imo: memoizedExtendedVesselInfo.imo,
        name: memoizedExtendedVesselInfo.name,
        requestType: 'ship-db',
        filteredOut: false,
      };
      shipDbResults.push(extendedVesselResult);
    }

    // Get database results (lowest priority)
    const databaseResults =
      (databaseSearchResult?.data as any)?.records?.map(
        (item: ShipSearchQuerystringGet200ResponseRecordsInner) => ({
          ...item,
          name: capitalizeName(item.name),
          requestType: 'database' as const,
          filteredOut: false,
        })
      ) || [];

    // Combine results in priority order and deduplicate by MMSI
    const seenMmsis = new Set<number>();
    const deduplicatedResults: ShipSearchResult[] = [];

    // Add results in priority order: AIS first, then ShipDB, then Database
    const allResultsInPriorityOrder = [
      ...aisResults,
      ...shipDbResults,
      ...databaseResults,
    ];

    for (const result of allResultsInPriorityOrder) {
      const mmsi = result.mmsi;

      // Skip if MMSI is invalid/null or already seen
      if (mmsi != null && !seenMmsis.has(mmsi)) {
        seenMmsis.add(mmsi);
        deduplicatedResults.push(result);
      } else if (mmsi == null) {
        // Always include results without MMSI (can't deduplicate them)
        deduplicatedResults.push(result);
      }
      // If MMSI already exists, skip this result (lower priority)
    }

    return deduplicatedResults;
  }, [
    aisSearchResult?.data,
    databaseSearchResult?.data,
    memoizedExtendedVesselInfo,
  ]);

  return {
    data,
    isLoading,
    isError: aisSearchResult.isError || databaseSearchResult.isError,
    error: aisSearchResult.error || databaseSearchResult.error,
  };
};
